/**
 * TCG Order List's Export Orders and Export Pull Sheet as CSV rows, in Retool's columns. The rows
 * come from `orders.ordersExport` and `orders.pullSheet`, read at the moment of the export
 * (Retool wrote both from the page's copy of the orders). Packing's pull sheet writes the same
 * file.
 */
import type { OrderExportRow, PullSheetRow } from "@loot-os/fulfillment-core/order/export-fold";
import { centsToDollars } from "./format";
import { type Dateish, toDate } from "./packing";

/** An exported order as the page receives it: its dates may cross the wire as ISO strings. */
export type OrderExportWireRow = Omit<OrderExportRow, "createdAt" | "shippedAt"> & {
  createdAt: Dateish;
  shippedAt: Dateish | null;
};

const ORDERS_HEADER = [
  "Order ID",
  "Tenant Reference",
  "Created At",
  "Shipped At",
  "Status",
  "Expected Order Cost",
  "Shipping Address",
  "Card Name",
  "Card Printing",
  "Card Link",
];

/**
 * Export Orders: one row per order, then one per live card beneath it, the cards' cells only.
 * Retool's columns, plus the tenant's reference beside the Loot OS id (Retool's Order ID was
 * boxed's own, which is the reference here).
 */
export function ordersExportRows(orders: OrderExportWireRow[]): unknown[][] {
  const rows: unknown[][] = [ORDERS_HEADER];
  for (const order of orders) {
    rows.push([
      order.orderId,
      order.tenantReferenceId ?? "",
      toDate(order.createdAt).toISOString(),
      order.shippedAt ? toDate(order.shippedAt).toISOString() : "",
      order.status,
      centsToDollars(order.expectedCostCents),
      order.address,
    ]);
    for (const card of order.cards) {
      rows.push(["", "", "", "", "", "", "", card.name, card.printing ?? "", card.link ?? ""]);
    }
  }
  return rows;
}

const PULL_SHEET_HEADER = [
  "Card Name",
  "Card Printing",
  "Card Link",
  "PENDING Count",
  "PACKED Count",
  "Count",
  "Avg Price",
];

/** Export Pull Sheet: one row per card and printing; Avg Price is Retool's, in dollars. */
export function pullSheetRows(rows: PullSheetRow[]): unknown[][] {
  return [
    PULL_SHEET_HEADER,
    ...rows.map((row) => [
      row.name,
      row.printing ?? "",
      row.link ?? "",
      row.pendingCount,
      row.packedCount,
      row.count,
      (row.totalValueCents / row.count / 100).toFixed(2),
    ]),
  ];
}

/**
 * `orders-2026-10-15T14-32-05.csv`: no colons, which Windows refuses in a file name, and these are
 * printed from warehouse desktops. The CSVs and the printed HTML files (`packingExports.ts`) share it.
 */
export function exportFileName(
  name: string,
  now: Date = new Date(),
  extension: "csv" | "html" = "csv",
): string {
  return `${name}-${now.toISOString().replace(/:/g, "-").replace(/\..+$/, "")}.${extension}`;
}
